import { useEffect, useRef, useState, useCallback } from 'react';
import './ForecastSimulationMobile.css';

/* ── Shorter feed text for mobile ── */
const PUBLIC_POOL = [
  '[Reuters] Fed signals rate pause through Q3',
  '[Bloomberg] EU carbon credits hit €98/ton',
  '[AP] US-China trade talks resume Geneva',
  '[WSJ] TSMC expands Arizona fab +40%',
  '[FT] UK gilt yields climb 14-year high',
  '[Polymarket] 72% on tariff reversal',
  '[GDELT] South China Sea tension +18.3',
  '[BLS] Non-farm payrolls +187K',
  '[API] Crude inventories −3.1M bbl',
  '[Reuters] ECB holds, signals Sept cut',
  '[Bloomberg] NVIDIA surpasses $4T cap',
  '[IMF] Global growth revised to 2.9%',
  '[AP] India bans rice exports',
  '[Reuters] Japan intervenes ¥162',
  '[Bloomberg] CPI 3.2% YoY (est 3.1%)',
];

const PRIVATE_POOL = [
  '[Satellite] Hormuz traffic −12%',
  '[Model] VIX regime shift detected',
  '[Expert] SEC enforcement incoming',
  '[Field] Foxconn output −22%',
  '[Model] Yield curve inversion signal',
  '[Satellite] China port surge +31%',
  '[Expert] WHO preparedness gaps',
  '[Field] Rare earth closures Ganzhou',
  '[Model] Credit spread widening',
  '[Expert] ASML export controls',
];

/* ── 3 agents (most distinct) ── */
const AGENTS = [
  {
    id: 0, name: 'Geo-Political', sme: true,
    targets: [0, 1],
    steps: [
      { action: 'researching', tool: 'Brave Search' },
      { action: 'cross-ref', tool: 'GDELT' },
      { action: 'estimating', tool: 'Models' },
    ],
  },
  {
    id: 1, name: 'Market', sme: false,
    targets: [0, 1],
    steps: [
      { action: 'scanning', tool: 'Bloomberg' },
      { action: 'pricing', tool: 'Options' },
    ],
  },
  {
    id: 2, name: 'Synthesis', sme: false,
    targets: [0, 1],
    steps: [
      { action: 'aggregating', tool: 'Outputs' },
      { action: 'bias audit', tool: 'Detection' },
      { action: 'calibrating', tool: 'Ensemble' },
    ],
  },
];

/* ── Reduced graph: 2 L0, 3 L1, 4 L2 ──
   Layout: horizontal spread, vertical layers flowing downward.
   SVG viewBox is 100x100; agents sit above in HTML, graph below. */
const NODES = [
  // L0 — top of graph area
  { id: 0, x: 30, y: 15, layer: 0 },
  { id: 1, x: 70, y: 15, layer: 0 },
  // L1
  { id: 2, x: 15, y: 50, layer: 1 },
  { id: 3, x: 50, y: 50, layer: 1 },
  { id: 4, x: 85, y: 50, layer: 1 },
  // L2 — terminal
  { id: 5, x: 10, y: 85, layer: 2 },
  { id: 6, x: 37, y: 85, layer: 2 },
  { id: 7, x: 63, y: 85, layer: 2 },
  { id: 8, x: 90, y: 85, layer: 2 },
];

const CHILDREN = {
  0: [2, 3],
  1: [3, 4],
  2: [5, 6],
  3: [6, 7],
  4: [7, 8],
};

const EDGES = [];
Object.entries(CHILDREN).forEach(([from, kids]) => {
  kids.forEach((to) => EDGES.push([Number(from), to]));
});

const NODE_MAP = {};
NODES.forEach((n) => { NODE_MAP[n.id] = n; });

const TERMINAL_IDS = NODES.filter((n) => n.layer === 2).map((n) => n.id);

export default function ForecastSimulationMobile() {
  const [running, setRunning] = useState(false);
  const [pubItems, setPubItems] = useState([]);
  const [priItems, setPriItems] = useState([]);
  const [forecasts, setForecasts] = useState(
    () => TERMINAL_IDS.map(() => 10 + Math.random() * 80)
  );
  const [fcFlash, setFcFlash] = useState(() => TERMINAL_IDS.map(() => false));

  const nodeAct = useRef({});
  const edgeAct = useRef({});
  const agentAct = useRef({});
  const agentStep = useRef({});
  const agentLine = useRef({});
  const agentGen = useRef({});
  const [, setTick] = useState(0);
  const bump = useCallback(() => setTick((v) => v + 1), []);

  const timersRef = useRef([]);
  const intervalsRef = useRef([]);
  const boxRef = useRef(null);
  const pubIdxRef = useRef(0);
  const priIdxRef = useRef(0);

  const timer = useCallback((fn, delay) => {
    const id = setTimeout(fn, delay);
    timersRef.current.push(id);
  }, []);

  const activate = useCallback((ref, key, dur) => {
    ref.current[key] = (ref.current[key] || 0) + 1;
    bump();
    timer(() => {
      ref.current[key] = Math.max(0, (ref.current[key] || 0) - 1);
      bump();
    }, dur);
  }, [bump, timer]);

  const isLit = (ref, key) => (ref.current[key] || 0) > 0;

  /* Propagation through graph */
  const propagate = useCallback((startId) => {
    activate(nodeAct, startId, 2600);
    const layers = {};
    const visited = new Set([startId]);
    const queue = [{ id: startId, depth: 0 }];
    while (queue.length > 0) {
      const { id, depth } = queue.shift();
      (CHILDREN[id] || []).forEach((child) => {
        if (!visited.has(child)) {
          visited.add(child);
          if (!layers[depth]) layers[depth] = [];
          layers[depth].push({ from: id, to: child });
          queue.push({ id: child, depth: depth + 1 });
        }
      });
    }

    Object.entries(layers).forEach(([depth, edges]) => {
      const delay = Number(depth) * 450 + 200;
      edges.forEach((e) => {
        timer(() => {
          activate(edgeAct, `${e.from}-${e.to}`, 1600);
          activate(nodeAct, e.to, 2000);

          const tIdx = TERMINAL_IDS.indexOf(e.to);
          if (tIdx !== -1) {
            setForecasts((prev) => {
              const next = [...prev];
              next[tIdx] = Math.max(1, Math.min(99,
                next[tIdx] + (Math.random() - 0.5) * 3));
              return next;
            });
            setFcFlash((prev) => {
              const next = [...prev];
              next[tIdx] = true;
              return next;
            });
            timer(() => {
              setFcFlash((prev) => {
                const next = [...prev];
                next[tIdx] = false;
                return next;
              });
            }, 800);
          }
        }, delay + Math.random() * 120);
      });
    });
  }, [activate, timer]);

  /* Fire single agent — no collaboration */
  const fireAgent = useCallback((agentIdx) => {
    if (isLit(agentAct, agentIdx)) return;

    const agent = AGENTS[agentIdx];
    const stepCount = agent.steps.length;
    const fireDelay = (stepCount - 1) * 900;
    const totalDur = stepCount * 900 + 600;

    const gen = (agentGen.current[agentIdx] || 0) + 1;
    agentGen.current[agentIdx] = gen;
    const isStale = () => agentGen.current[agentIdx] !== gen;

    agentStep.current[agentIdx] = -1;
    activate(agentAct, agentIdx, totalDur);

    // Cycle steps
    agent.steps.forEach((_, si) => {
      timer(() => {
        if (isStale()) return;
        agentStep.current[agentIdx] = si;
        bump();
      }, si * 900);
    });

    // Fire line into graph
    timer(() => {
      if (isStale()) return;
      const nId = agent.targets[Math.floor(Math.random() * agent.targets.length)];
      const lineKey = `${agentIdx}-${nId}`;
      activate(agentLine, lineKey, 1400);
      timer(() => {
        if (isStale()) return;
        propagate(nId);
      }, 350);
    }, fireDelay);

    // Clear step
    timer(() => {
      if (isStale()) return;
      agentStep.current[agentIdx] = -1;
      bump();
    }, totalDur);
  }, [activate, timer, bump, propagate]);

  /* Start / stop */
  const startSim = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    intervalsRef.current.forEach(clearInterval);
    timersRef.current = [];
    intervalsRef.current = [];
    nodeAct.current = {};
    edgeAct.current = {};
    agentAct.current = {};
    agentStep.current = {};
    agentLine.current = {};
    agentGen.current = {};
    setPubItems([]);
    setPriItems([]);
    setForecasts(TERMINAL_IDS.map(() => 10 + Math.random() * 80));
    setFcFlash(TERMINAL_IDS.map(() => false));
    pubIdxRef.current = 0;
    priIdxRef.current = 0;
    setRunning(true);

    // Public feed — every 2s, max 3
    timer(() => {
      setPubItems([{ id: Date.now(), text: PUBLIC_POOL[0] }]);
      pubIdxRef.current = 1;
    }, 200);
    intervalsRef.current.push(setInterval(() => {
      const idx = pubIdxRef.current % PUBLIC_POOL.length;
      pubIdxRef.current++;
      setPubItems((prev) => [{ id: Date.now(), text: PUBLIC_POOL[idx] }, ...prev].slice(0, 3));
    }, 2000));

    // Private feed — every 5s, max 3
    timer(() => {
      setPriItems([{ id: Date.now(), text: PRIVATE_POOL[0] }]);
      priIdxRef.current = 1;
    }, 1200);
    intervalsRef.current.push(setInterval(() => {
      const idx = priIdxRef.current % PRIVATE_POOL.length;
      priIdxRef.current++;
      setPriItems((prev) => [{ id: Date.now(), text: PRIVATE_POOL[idx] }, ...prev].slice(0, 3));
    }, 5000));

    // Agent firing — 10-18s per agent
    AGENTS.forEach((_, i) => {
      const baseDelay = 2000 + i * 1500;
      const interval = 10000 + Math.floor(Math.random() * 8000) + i * 500;
      timer(() => fireAgent(i), baseDelay);
      intervalsRef.current.push(setInterval(() => fireAgent(i), interval));
    });
  }, [timer, fireAgent]);

  const stopSim = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    intervalsRef.current.forEach(clearInterval);
    timersRef.current = [];
    intervalsRef.current = [];
    setRunning(false);
  }, []);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) startSim(); else stopSim(); },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => { obs.disconnect(); stopSim(); };
  }, [startSim, stopSim]);

  const getStep = (id) => agentStep.current[id] ?? -1;

  /* Agent card center-x positions for SVG lines (percentage) */
  const AGENT_CX = [17, 50, 83]; // 3 agents spread across width

  return (
    <div className="sim-m" ref={boxRef}>
      {/* ── FEEDS ── */}
      <div className="sim-m__feeds">
        <div className="sim-m__feed">
          <div className="sim-m__feed-hdr">
            <span className="sim-m__feed-dot sim-m__feed-dot--pub" />
            <span className="sim-m__feed-label">Public</span>
          </div>
          <div className="sim-m__feed-stream">
            {pubItems.map((item) => (
              <div key={item.id} className="sim-m__fi">{item.text}</div>
            ))}
          </div>
        </div>
        <div className="sim-m__feed">
          <div className="sim-m__feed-hdr">
            <span className="sim-m__feed-dot sim-m__feed-dot--pri" />
            <span className="sim-m__feed-label">Private</span>
          </div>
          <div className="sim-m__feed-stream">
            {priItems.map((item) => (
              <div key={item.id} className="sim-m__fi sim-m__fi--pri">{item.text}</div>
            ))}
          </div>
        </div>
      </div>

      {/* ── AGENTS ── */}
      <div className="sim-m__agents">
        {AGENTS.map((a) => {
          const active = isLit(agentAct, a.id);
          const step = getStep(a.id);
          return (
            <div key={a.id} className={`sim-m__acard${active ? ' sim-m__acard--active' : ''}`}>
              <div className="sim-m__acard-head">
                <span className={`sim-m__acard-type${a.sme ? ' sim-m__acard-type--sme' : ''}`}>
                  {a.sme ? 'SME' : 'GEN'}
                </span>
                <span className="sim-m__acard-name">{a.name}</span>
              </div>
              <div className="sim-m__acard-steps">
                {a.steps.map((s, si) => (
                  <div key={si} className={`sim-m__acard-row${active && si === step ? ' sim-m__acard-row--active' : ''}${active && si < step ? ' sim-m__acard-row--done' : ''}`}>
                    <span className="sim-m__acard-row-num">{si + 1}</span>
                    <span className="sim-m__acard-row-action">{s.action}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── GRAPH ── */}
      <div className="sim-m__graph">
        <svg className="sim-m__graph-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
          {/* Agent → L0 lines */}
          {AGENTS.map((agent) =>
            agent.targets.map((nId) => {
              const target = NODE_MAP[nId];
              const lineKey = `${agent.id}-${nId}`;
              const lit = isLit(agentLine, lineKey);
              return (
                <line key={`a${agent.id}-n${nId}`}
                  x1={AGENT_CX[agent.id]} y1="0"
                  x2={target.x} y2={target.y}
                  className={`sim-m__aline${lit ? ' sim-m__aline--on' : ''}`}
                />
              );
            })
          )}

          {/* Graph edges */}
          {EDGES.map(([from, to], i) => (
            <line key={`e${i}`}
              x1={NODE_MAP[from].x} y1={NODE_MAP[from].y}
              x2={NODE_MAP[to].x} y2={NODE_MAP[to].y}
              className={`sim-m__gedge${isLit(edgeAct, `${from}-${to}`) ? ' sim-m__gedge--on' : ''}`}
            />
          ))}

          {/* Outflow lines from terminal nodes → bottom */}
          {TERMINAL_IDS.map((nId, i) => (
            <line key={`of${i}`}
              x1={NODE_MAP[nId].x} y1={NODE_MAP[nId].y}
              x2={NODE_MAP[nId].x} y2="100"
              className={`sim-m__outflow${isLit(nodeAct, nId) ? ' sim-m__outflow--on' : ''}`}
            />
          ))}
        </svg>

        {/* Nodes */}
        {NODES.map((n) => (
          <div key={`n${n.id}`}
            className={`sim-m__gnode${isLit(nodeAct, n.id) ? ' sim-m__gnode--on' : ''} sim-m__gnode--l${n.layer}`}
            style={{ left: `${n.x}%`, top: `${n.y}%` }}
          />
        ))}
      </div>

      {/* ── OUTPUTS ── */}
      <div className="sim-m__outputs">
        {forecasts.map((val, i) => (
          <div key={i}
            className={`sim-m__fc${running ? ' sim-m__fc--on' : ''}${fcFlash[i] ? ' sim-m__fc--flash' : ''}`}
          >
            <span className="sim-m__fc-val">{val.toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}
